// A reference names the stage shape a picture is framed on, and how: `{ id, mode?, edge?, offset?, from?, to?, bind? }`. Opaque to the field contract.
import { wrapSegment } from './path'

/** `mode`: 'edge' runs along segment `edge`; 'perspective' takes a convex quad's (any other shape renders as 'edge'); 'screen' squares to the screen; 'stage' windows the node's picture; 'flow' runs from segment `from` to segment `to`. `offset` rotates on top in every mode. */
export const REF_DEFAULTS = { mode: 'edge', edge: 0, offset: 0 }

/** A reference with every default filled. */
export const normalizeRef = ref => ({ ...REF_DEFAULTS, ...ref })

/** One reference's identity, blind to property order and to defaults spelled out or left absent. */
export const refKey = ref => {
	const r = normalizeRef(ref)
	return `${r.id}:${r.mode}:${r.edge}:${r.offset}:${r.from ?? ''}:${r.to ?? ''}`
}

/** A reference list's identity, blind to property order and to defaults spelled out or left absent. `~/lib/outline`'s `refsKey` adds the outlines' geometry. */
export const refsKey = refs => (refs ?? []).map(refKey).join('|')

const SEGMENT_KEYS = ['edge', 'from', 'to']

// The segment keys a reference names: `edge` where its mode reads it or it is stored, a flow end where stored.
const segmentKeys = ref =>
	SEGMENT_KEYS.filter(k =>
		k === 'edge'
			? ref.mode === 'edge' || ref.mode === 'perspective' || Number.isFinite(ref.edge)
			: Number.isFinite(ref[k])
	)

const round = v => Math.round(v * 10) / 10

// Segment i's endpoints, flat: `[x0, y0, x1, y1]`.
const endsOf = (anchors, i) => {
	const a = anchors[i]
	const b = anchors[(i + 1) % anchors.length]
	return [a[0], a[1], b[0], b[1]].map(round)
}

const nearestSegment = (anchors, [x0, y0, x1, y1]) => {
	let best = 0
	let far = Number.POSITIVE_INFINITY
	for (let i = 0; i < anchors.length; i++) {
		const [a0, b0, a1, b1] = endsOf(anchors, i)
		const d = (a0 - x0) ** 2 + (b0 - y0) ** 2 + (a1 - x1) ** 2 + (b1 - y1) ** 2
		if (d < far) {
			far = d
			best = i
		}
	}
	return best
}

export const bindSegments = (ref, anchors) => {
	const n = anchors?.length ?? 0
	const { bind: _bind, ...rest } = ref
	const keys = n ? segmentKeys(rest) : []
	if (!keys.length) return rest
	const bind = { n }
	for (const k of keys) bind[k] = endsOf(anchors, wrapSegment(rest[k] ?? 0, n))
	return { ...rest, bind }
}

export const rebindSegments = (ref, anchors) => {
	const n = anchors?.length ?? 0
	const was = ref?.bind
	if (!n) return ref
	if (!was || was.n === n) {
		const out = bindSegments(ref, anchors)
		return was?.moved && out.bind ? { ...out, bind: { ...out.bind, moved: true } } : out
	}
	const next = { ...ref }
	for (const k of SEGMENT_KEYS) if (was[k]) next[k] = nearestSegment(anchors, was[k])
	if (next.edge === 0) delete next.edge
	const out = bindSegments(next, anchors)
	return out.bind ? { ...out, bind: { ...out.bind, moved: true } } : out
}
